import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Polyline, CircleMarker, Polygon, useMap } from "react-leaflet";
import L from "leaflet";
import { http, zonesApi } from "@/lib/api";
import { Play, Pause, ArrowCounterClockwise, ClockCounterClockwise, FastForward, Rewind } from "@phosphor-icons/react";
import { STATUS_CONFIG } from "@/lib/constants";

const ASSET_COLORS = ["#ff8c00", "#4682b4", "#22c55e", "#a855f7", "#eab308", "#ef4444", "#06b6d4", "#ec4899", "#10b981", "#f97316", "#8b5cf6", "#f43f5e"];

function FitBounds({ trails }) {
  const map = useMap();
  useEffect(() => {
    const pts = [];
    Object.values(trails).forEach((arr) => arr.forEach((p) => pts.push([p.lat, p.lng])));
    if (pts.length > 0) {
      map.fitBounds(L.latLngBounds(pts), { padding: [60, 60], maxZoom: 13 });
    }
  }, [trails]); // eslint-disable-line
  return null;
}

export default function HistoryPage() {
  const [hours, setHours] = useState(1);
  const [trails, setTrails] = useState({}); // assetId -> [points]
  const [assetNames, setAssetNames] = useState({});
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(0); // 0..1
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const playRef = useRef(null);

  const load = async (h) => {
    setLoading(true);
    try {
      const [trailRes, zoneRes] = await Promise.all([
        http.get(`/trails/range?hours=${h}`).then((r) => r.data),
        zonesApi.list(),
      ]);
      setTrails(trailRes.trails || {});
      setAssetNames(trailRes.asset_names || {});
      setZones(zoneRes);
      setCursor(1);
    } catch (e) {
      // noop
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(hours);
  }, [hours]);

  // Compute timeline window
  const { totalMs, startTs, endTs, maxLen } = useMemo(() => {
    let mn = Infinity, mx = -Infinity, ml = 0;
    Object.values(trails).forEach((arr) => {
      if (arr.length === 0) return;
      ml = Math.max(ml, arr.length);
      const first = new Date(arr[0].ts).getTime();
      const last = new Date(arr[arr.length - 1].ts).getTime();
      if (first < mn) mn = first;
      if (last > mx) mx = last;
    });
    if (!isFinite(mn)) { mn = 0; mx = 0; }
    return { totalMs: Math.max(1, mx - mn), startTs: mn, endTs: mx, maxLen: ml };
  }, [trails]);

  const cursorTs = useMemo(() => startTs + cursor * totalMs, [cursor, startTs, totalMs]);

  // For each asset, slice trail up to cursorTs
  const slices = useMemo(() => {
    const out = {};
    Object.entries(trails).forEach(([aid, pts]) => {
      const slice = pts.filter((p) => new Date(p.ts).getTime() <= cursorTs);
      out[aid] = slice;
    });
    return out;
  }, [trails, cursorTs]);

  // Playback loop
  useEffect(() => {
    if (!playing) {
      if (playRef.current) cancelAnimationFrame(playRef.current);
      return;
    }
    let lastFrame = performance.now();
    const step = (t) => {
      const dt = t - lastFrame;
      lastFrame = t;
      // Map total playback duration to ~30s base, then divide by speed
      const playbackMs = 30000 / speed;
      setCursor((c) => {
        const next = c + dt / playbackMs;
        if (next >= 1) { setPlaying(false); return 1; }
        return next;
      });
      playRef.current = requestAnimationFrame(step);
    };
    playRef.current = requestAnimationFrame(step);
    return () => { if (playRef.current) cancelAnimationFrame(playRef.current); };
  }, [playing, speed]);

  const assetIds = Object.keys(trails);
  const colorFor = (aid) => {
    const idx = assetIds.indexOf(aid);
    return ASSET_COLORS[idx % ASSET_COLORS.length];
  };

  const fmtTime = (ms) => ms > 0 ? new Date(ms).toLocaleTimeString() : "—";

  return (
    <div className="h-[calc(100vh-72px)] flex flex-col" data-testid="history-page">
      <div className="border-b border-[#2a2a2a] p-4 bg-[#0d0d0d] flex flex-wrap items-center gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">Historical Playback</div>
          <h2 className="font-heading font-black text-xl tracking-tight">Fleet Trail Replay</h2>
        </div>

        <div className="flex items-center gap-1 ml-4">
          {[1, 3, 6, 12, 24].map((h) => (
            <button
              key={h}
              onClick={() => setHours(h)}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold border transition-colors ${
                hours === h ? "bg-[#ff8c00] text-black border-[#ff8c00]" : "border-[#2a2a2a] text-[#a1a1aa] hover:border-[#4d4d4d]"
              }`}
              data-testid={`window-${h}h`}
            >
              Last {h}h
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-3 font-mono text-xs text-[#a1a1aa]">
          <div>
            <span className="text-[#71717a] uppercase tracking-wider text-[10px] mr-2">Assets</span>
            <span className="font-bold text-white">{assetIds.length}</span>
          </div>
          <div>
            <span className="text-[#71717a] uppercase tracking-wider text-[10px] mr-2">Points</span>
            <span className="font-bold text-white">{Object.values(trails).reduce((a, b) => a + b.length, 0)}</span>
          </div>
        </div>
      </div>

      <div className="flex-1 relative">
        <MapContainer
          center={[47.6062, -122.3321]}
          zoom={12}
          style={{ height: "100%", width: "100%", background: "#111" }}
          data-testid="history-map"
        >
          <TileLayer
            attribution="&copy; OSM"
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <FitBounds trails={trails} />

          {zones.map((z) => (
            <Polygon
              key={z.id}
              positions={z.polygon}
              pathOptions={{
                color: z.color, fillColor: z.color, fillOpacity: 0.08, weight: 1,
                dashArray: z.kind === "restricted" ? "8,6" : null,
              }}
            />
          ))}

          {Object.entries(slices).map(([aid, pts]) => {
            if (!pts || pts.length === 0) return null;
            const color = colorFor(aid);
            const head = pts[pts.length - 1];
            return (
              <div key={aid}>
                {pts.length > 1 && (
                  <Polyline
                    positions={pts.map((p) => [p.lat, p.lng])}
                    pathOptions={{ color, weight: 3, opacity: 0.7 }}
                  />
                )}
                <CircleMarker
                  center={[head.lat, head.lng]}
                  radius={7}
                  pathOptions={{ color: "#fff", fillColor: color, fillOpacity: 1, weight: 2 }}
                />
              </div>
            );
          })}
        </MapContainer>

        {/* Asset legend */}
        <div className="absolute top-4 left-4 z-[1000] bg-black/70 backdrop-blur-xl border border-white/10 p-3 max-h-[40vh] overflow-y-auto w-[240px]" data-testid="asset-legend">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#71717a] mb-2">Fleet Trails</div>
          {assetIds.length === 0 && (
            <div className="text-xs text-[#71717a] uppercase tracking-wider py-3 text-center">
              {loading ? "Loading…" : "No movement data in this window"}
            </div>
          )}
          {assetIds.map((aid) => {
            const len = slices[aid]?.length || 0;
            const total = trails[aid]?.length || 0;
            return (
              <div key={aid} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5" style={{ background: colorFor(aid) }}></span>
                  <span className="text-xs font-mono font-bold">{assetNames[aid] || aid.slice(0, 6)}</span>
                </div>
                <span className="text-[10px] font-mono text-[#71717a]">{len}/{total}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Playback controls */}
      <div className="border-t border-[#2a2a2a] bg-[#0d0d0d] p-4 flex items-center gap-4" data-testid="playback-controls">
        <button
          onClick={() => setCursor(0)}
          className="w-10 h-10 border border-[#2a2a2a] hover:border-[#ff8c00] flex items-center justify-center transition-colors"
          data-testid="restart-btn"
        >
          <ArrowCounterClockwise size={16} />
        </button>
        <button
          onClick={() => setPlaying(!playing)}
          className="w-12 h-12 bg-[#ff8c00] text-black hover:bg-[#e67e00] flex items-center justify-center transition-colors"
          data-testid="play-btn"
        >
          {playing ? <Pause size={18} weight="fill" /> : <Play size={18} weight="fill" />}
        </button>

        <div className="flex-1 flex items-center gap-3">
          <span className="font-mono text-xs text-[#a1a1aa]">{fmtTime(startTs)}</span>
          <div className="flex-1 relative h-2 bg-[#1a1a1a] border border-[#2a2a2a]">
            <input
              type="range"
              min={0}
              max={1}
              step={0.001}
              value={cursor}
              onChange={(e) => setCursor(parseFloat(e.target.value))}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              data-testid="timeline-slider"
            />
            <div
              className="absolute inset-y-0 left-0 bg-[#ff8c00]"
              style={{ width: `${cursor * 100}%` }}
            ></div>
            <div
              className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white border border-[#ff8c00]"
              style={{ left: `calc(${cursor * 100}% - 6px)` }}
            ></div>
          </div>
          <span className="font-mono text-xs text-[#a1a1aa]">{fmtTime(endTs)}</span>
        </div>

        <div className="flex items-center gap-1 border border-[#2a2a2a]">
          {[0.5, 1, 2, 4].map((s) => (
            <button
              key={s}
              onClick={() => setSpeed(s)}
              className={`px-3 py-2 text-[10px] uppercase tracking-[0.2em] font-bold ${
                speed === s ? "bg-[#4682b4] text-white" : "text-[#a1a1aa] hover:bg-[#1a1a1a]"
              }`}
              data-testid={`speed-${s}x`}
            >
              {s}x
            </button>
          ))}
        </div>

        <div className="font-mono text-xs text-[#ff8c00] min-w-[140px] text-right">
          <ClockCounterClockwise size={12} className="inline mr-1" />
          {fmtTime(cursorTs)}
        </div>
      </div>
    </div>
  );
}
